import React from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import { mugs, blankCups } from "../mock";
import { ArrowRight, Upload, FileCheck, Check } from "lucide-react";

export default function CupsDrinkware() {
  return (
    <PageShell kicker="Cups & Drinkware" title="Personalized cups and drinkware" subtitle="A daily reminder of God's promises with every sip. Make your own with a picture you love, or pick one of the designs we've already made.">

      {/* Two ways to fill your cup */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="text-center">
          <p className="kicker">Where would you like to start?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Two ways to fill your cup</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-5 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Both are right here on this page — start your own from scratch, or scroll down to the designs that are ready to ship.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          <a href="#create-your-own" className="group bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 hover-lift">
            <div className="aspect-[4/3] overflow-hidden bg-[color:var(--dms-peach-light)]/30">
              <img src={blankCups[0].img} alt="Blank white ceramic mug" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-7">
              <p className="kicker text-[11px]">Option one</p>
              <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-2">Create your own</h3>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">Pick a 12 oz mug or a 16 oz frosted glass, send us your picture or the words you want, and we'll print it just for you.</p>
              <p className="link-peach mt-4 inline-flex text-[11px]">Start designing <ArrowRight className="w-3.5 h-3.5" /></p>
            </div>
          </a>
          <a href="#ready-made" className="group bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 hover-lift">
            <div className="aspect-[4/3] overflow-hidden bg-[color:var(--dms-peach-light)]/30">
              <img src={mugs[0].img} alt="Ready-made Scripture mug" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-7">
              <p className="kicker text-[11px]">Option two</p>
              <h3 className="font-serif text-2xl text-[color:var(--dms-navy)] mt-2">Shop our ready-made designs</h3>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">Already drawn, already loved. Browse {mugs.length} Scripture cups and mugs you can order exactly as they are.</p>
              <p className="link-peach mt-4 inline-flex text-[11px]">See all designs <ArrowRight className="w-3.5 h-3.5" /></p>
            </div>
          </a>
        </div>
      </section>

      {/* Create your own */}
      <section id="create-your-own" className="bg-[color:var(--dms-cream-2)] py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 text-center">
          <p className="kicker">Option one</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Create your own</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-5 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Choose the cup, add the picture you want on it, and tell us anything else. We'll send a proof before we print a thing.</p>
        </div>
        <div className="max-w-6xl mx-auto px-5 lg:px-8 mt-12">
          <StepHeader n="1" title="Choose your cup" />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {blankCups.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10 flex flex-col">
                <div className="aspect-[4/3] overflow-hidden bg-[color:var(--dms-peach-light)]/30">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">{c.title}</h3>
                  <p className="mt-2 text-sm text-[color:var(--dms-navy)]/65">{c.desc}</p>
                  <ul className="mt-4 space-y-2">
                    {c.bullets.map((b) => (<li key={b} className="flex items-start gap-2 text-sm text-[color:var(--dms-navy)]/75"><Check className="w-4 h-4 text-[color:var(--dms-peach)] mt-0.5" /> {b}</li>))}
                  </ul>
                  <p className="mt-5 font-serif text-2xl text-[color:var(--dms-navy)]">{c.price}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10"><StepHeader n="2" title="Put your picture on it" /></div>
          <label className="mt-5 block bg-white border-2 border-dashed border-[color:var(--dms-navy)]/20 rounded-2xl p-10 text-center cursor-pointer hover:border-[color:var(--dms-peach)] transition">
            <Upload className="w-8 h-8 mx-auto text-[color:var(--dms-peach)]" />
            <p className="mt-3 font-serif text-lg text-[color:var(--dms-navy)]">Drop your design here, or click to browse</p>
            <p className="text-xs text-[color:var(--dms-navy)]/60 mt-1">PNG, JPG, SVG or PDF · up to 4 files · 10 MB each</p>
            <input type="file" className="hidden" />
          </label>
          <p className="mt-3 text-xs text-[color:var(--dms-navy)]/60 text-center max-w-xl mx-auto">Your files stay on this page for now — we'll ask you to send them over when we confirm your order, and you'll always see a proof before anything is printed.</p>

          <div className="mt-10"><StepHeader n="3" title="Any words or notes?" note="Optional" /></div>
          <textarea rows={4} placeholder="Tell us any words you want printed, or anything we should know." className="mt-5 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)]" />

          <div className="mt-10 text-center">
            <Link to="/custom-design" className="btn-peach">Add to Order <ArrowRight className="w-4 h-4" /></Link>
            <p className="mt-4 text-xs text-[color:var(--dms-navy)]/60 flex items-center justify-center gap-2"><FileCheck className="w-3.5 h-3.5" /> Nothing is charged yet. We look over your artwork, send you a proof, and only print once you say yes.</p>
          </div>
        </div>
      </section>

      {/* Ready-made designs */}
      <section id="ready-made" className="py-16">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 text-center">
          <p className="kicker">Option two</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">All the designs we have ready</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-5 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Every one of these is drawn, printed and ready to order just as you see it — no waiting on a proof.</p>
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {mugs.map((m) => (
            <Link to="/custom-design" key={m.id} className="group bg-white rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/5 hover-lift">
              <div className="aspect-square overflow-hidden bg-[color:var(--dms-peach-light)]/30">
                <img src={m.img} alt={m.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <p className="kicker text-[10px]">{m.category}</p>
                <h3 className="font-serif text-base mt-2 text-[color:var(--dms-navy)] leading-snug line-clamp-2">{m.title}</h3>
                <p className="mt-1 text-xs text-[color:var(--dms-navy)]/55">{m.note}</p>
                <p className="mt-3 font-serif text-lg text-[color:var(--dms-navy)]">{m.price}</p>
                <p className="mt-3 text-[10px] tracking-widest uppercase font-semibold text-[color:var(--dms-navy)] group-hover:text-[color:var(--dms-peach)] transition-colors">View product →</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-[color:var(--dms-navy)] py-16">
        <div className="max-w-3xl mx-auto text-center px-5">
          <h3 className="font-serif text-2xl md:text-3xl text-white">Didn't find the one?</h3>
          <p className="mt-4 text-white/75">Send us your favorite verse, a family photo or your church logo. We'll set it on a mug or a glass and show you how it looks before anything is printed.</p>
          <div className="mt-8"><Link to="/custom-design" className="btn-peach">Make your own <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </PageShell>
  );
}

function StepHeader({ n, title, note }) {
  return (
    <div className="flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center text-[color:var(--dms-navy)] font-serif text-lg">{n}</div>
      <div>
        <h3 className="font-serif text-2xl text-[color:var(--dms-navy)]">{title}</h3>
        {note && <p className="text-xs tracking-widest uppercase text-[color:var(--dms-navy)]/50 mt-0.5">{note}</p>}
      </div>
    </div>
  );
}
